import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ActiveSession } from '@/hooks/useDashboardData';

interface PhaseDistributionChartProps {
  sessions: ActiveSession[];
}

export const PhaseDistributionChart = ({ sessions }: PhaseDistributionChartProps) => {
  const chartData = useMemo(() => {
    // Phase 1: Analyse, Phase 2: Optionen, Phase 3: Bewertung (3.0-3.3), Phase 4: Report/Abgeschlossen (3.4)
    const phaseCounts = { 1: 0, 2: 0, 3: 0, 4: 0 };

    sessions.forEach((session) => {
      const subphase = session.subphase || `${session.phase}.0`;
      
      if (subphase === '3.4') {
        // Subphase 3.4 = Report fertig = "Phase 4"
        phaseCounts[4] += 1;
      } else if (session.phase === 3) {
        // Phase 3 aber nicht 3.4 = Bewertung
        phaseCounts[3] += 1;
      } else {
        phaseCounts[session.phase] = (phaseCounts[session.phase] || 0) + 1;
      }
    });

    return [
      { phase: 'Phase 1', entscheidungen: phaseCounts[1], fill: 'hsl(var(--chart-1))' },
      { phase: 'Phase 2', entscheidungen: phaseCounts[2], fill: 'hsl(var(--chart-2))' },
      { phase: 'Phase 3', entscheidungen: phaseCounts[3], fill: 'hsl(var(--chart-3))' },
      { phase: 'Abgeschlossen', entscheidungen: phaseCounts[4], fill: 'hsl(var(--chart-4))' },
    ];
  }, [sessions]);

  if (sessions.length === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
        Noch keine Daten verfügbar
      </div>
    );
  }

  const maxValue = Math.max(...chartData.map(d => d.entscheidungen), 1);

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis 
          dataKey="phase" 
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          axisLine={{ stroke: 'hsl(var(--border))' }}
          tickLine={false}
        />
        <YAxis 
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          domain={[0, Math.ceil(maxValue * 1.2)]}
          width={35}
        />
        <Tooltip 
          contentStyle={{
            backgroundColor: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            color: 'hsl(var(--popover-foreground))',
            fontSize: '13px',
          }}
          formatter={(value: number) => [value, 'Entscheidungen']}
          cursor={{ fill: 'hsl(var(--muted)/0.1)' }}
        />
        <Bar 
          dataKey="entscheidungen" 
          fill="hsl(var(--primary))"
          radius={[6, 6, 0, 0]}
          maxBarSize={60}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
